import {
  Stack,
  Typography,
  Grid,
  Divider,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { ModalSheetDialog as Dialog } from './styledComponents';
import { CancelButtonStyled } from '@v2/components/campaign/CampaignAdGroupSection/styles';
import { Button } from '@v2/components/ui/Button';
import { Controller, FormProvider } from 'react-hook-form';
import { useMemo } from 'react';
import numeral from 'numeral';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { FormWrapperStyled } from '@v2/components/campaign/CampaignAdGroupSection/AdvancedSubflows/AdvancedAdGroupDelivery/styles';
import { useBidStrategyForm } from '../hooks/useBidStrategyForm';
import { BidStrategyOptionsAccordion } from './BidStrategyOptionsAccordion';
import { BidStrategyAccordionOption } from './BidStrategyAccordion';
import { EditBidStrategyProps } from '../constants/types';
import { useBulkEdit } from '../context';

export const EditBidStrategy = ({
  fieldValues,
  bidStrategyOptions = [],
  recommendedBidRange = [10, 20],
  onSubmit,
  onCancel,
  onBack,
  isValidating = false,
  open
}: EditBidStrategyProps) => {
  const getDefaultValues = () => {
    if (!fieldValues || fieldValues === 'None') return undefined;

    return {
      bidStrategy: fieldValues.bid_strategy,
      maxCPMBid: fieldValues.cpm ? parseFloat(fieldValues.cpm) : null,
      bidStrategyTarget: fieldValues.bid_strategy_target,
      bidStrategyEvent: fieldValues.bid_strategy_event,
    };
  };

  const {
    methods,
    control,
    isMaxCPMBidEnabled,
    handleCPMSwitchChange,
    isValid,
  } = useBidStrategyForm({
    defaultValues: getDefaultValues(),
    bidStrategyOptions: bidStrategyOptions.map(option => ({
      value: option.value,
      label: option.label,
      description: option.description,
      locked: option.locked,
      infoText: option.infoText,
    })),
    onSubmit,
  });

  const rangeText = useMemo(() => {
    if (!recommendedBidRange || recommendedBidRange.length === 0) return 'not available';

    const min = recommendedBidRange[0];
    const max = recommendedBidRange[recommendedBidRange.length - 1];

    const formattedRange =
      min === max
        ? `${numeral(Math.ceil(min)).format('$0,0')}`
        : `${numeral(min).format('$0,0')} - ${numeral(Math.ceil(max)).format(
            '0,0'
          )}`;

    return `The recommended bid range for your inventory selection is ${formattedRange}.`;
  }, [recommendedBidRange]);

  const formContext = useMemo(
    () => ({
      control,
      isMaxCPMBidEnabled,
      handleCPMSwitchChange,
      rangeText,
    }),
    [control, isMaxCPMBidEnabled, handleCPMSwitchChange, rangeText]
  );

  const finalOptions = useMemo<BidStrategyAccordionOption[]>(() => {
    return bidStrategyOptions.map(option => {
      const baseOption = {
        value: String(option.value),
        label: option.label,
        description: option.description,
        infoText: option.infoText,
      };

      if (option.locked) {
        return {
          ...baseOption,
          locked: true,
          lockedInfo: option.lockedInfo
            ? String(option.lockedInfo)
            : 'This option is currently unavailable',
        };
      }

      return {
        ...baseOption,
        locked: false,
        unlockedContent: option.renderContent(formContext),
      };
    });
  }, [bidStrategyOptions, formContext]);

  const { getHeaderText } = useBulkEdit();

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={onCancel}
        fullWidth
        maxWidth="lg"
        sx={{ pt: 2, pb: 2 }}
        data-testid="edit-bid-strategy-dialog"
      >
        <form id="bulk-edit-form" onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              pt={1}
            >
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={onBack}
                  size="large"
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h3">{getHeaderText()}</Typography>
              </Box>

              <IconButton
                onClick={onCancel}
                size="large"
                aria-label="close"
              >
                <CloseIcon data-testid="CloseIcon" />
              </IconButton>
            </Box>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ p: 3 }}>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={6}>
                <Stack sx={{ height: '100%' }}>
                  <FormWrapperStyled>
                    <Stack spacing={3}>
                      {isValidating ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <Controller
                          name="bidStrategy"
                          control={control}
                          render={({ field }) => (
                            <BidStrategyOptionsAccordion
                              value={field.value}
                              options={finalOptions}
                              onChange={newValue => {
                                field.onChange(newValue);
                              }}
                            />
                          )}
                        />
                      )}
                    </Stack>
                  </FormWrapperStyled>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions disableSpacing sx={{ px: 4, py: 3 }}>
            <Grid container direction="column" sx={{ width: '100%' }}>
              <Divider sx={{ width: '100%', mb: 3 }} />
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ minHeight: 'auto' }}
              >
                <Grid>
                  <CancelButtonStyled variant="text" onClick={onCancel}>
                    Cancel
                  </CancelButtonStyled>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    type="submit"
                    label="Next: Review Edits"
                    disabled={!isValid}
                    sx={{ visibility: isValidating ? 'hidden' : 'visible' }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </FormProvider>
  );
};
